import styled, { css } from 'styled-components';
import { NavLink } from 'react-router-dom';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import {
  svg,
  plainCard,
  plainCardGroup,
  color,
  text_s,
  text_xscap,
  linkStyle,
  safeFocusRemoval,
  focusOutline,
  hideOnPrint,
  heading_xl,
  heading_l,
  heading_m,
  heading_s,
  heading_xs
} from '_utils/styles';
import { spacing } from '_utils/props';
import ContentRichText from '../ContentRichText';

const detailColor = color('element.quaternary');

export const Card = styled.article<{$hasHeading: boolean}>`
  ${({ theme }) => plainCard({ theme })}
  overflow: hidden;
  @media print {
    page-break-inside: avoid;
  }
  ${(props) => props.$hasHeading && css`
    ${({ theme }) => spacing({ theme, $mt: 1 })}
  `}
`;

export const Group = styled.div`
  ${({ theme }) => plainCardGroup({ theme })}
  ${text_s}
  margin: 0;
`;

export const ProfileHeading = styled.h2`
  ${heading_s}
  margin: 0;
`;

export const Role = styled.p`
  ${text_xscap}
  color: ${detailColor};
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 1 })}
`;

export const Description = styled.p`
  ${text_s}
  color: ${detailColor};
  margin: 0;
  ${({ theme }) => spacing({ theme, $mt: 1 })}
`;

export const DescriptionListWrapper = styled.dl<{$collapsed: boolean}>`
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding-bottom: 4px;

  max-height: ${({ $collapsed }) => ($collapsed ? '0px' : '500px')};
  opacity: ${({ $collapsed }) => ($collapsed ? '0' : '1')};
  overflow: hidden;
  transition: max-height 500ms, opacity 400ms;
  @media print {
    opacity: 1;
    max-height: 500px;
  }
`;

export const DescriptionTerm = styled.dt`
  ${svg(16, 16)}
  width: 16px;
  position: relative;
  top: 4px;
  ${({ theme }) => spacing({ theme, $mt: 2 })}

  path {
    fill: ${detailColor};
  }
`;

export const DescriptionDetails = styled.dd`
  ${text_s}
  font-style: normal;
  margin: 0;
  ${({ theme }) => spacing({ theme, $pl: 1, $mt: 2 })}
  width: calc(100% - 16px);
`;

export const Ellipsis = styled.span`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LinkStyles = css`
  color: ${({ theme }) => theme.colors.element.primary};
  width: calc(100% - 4px);
  display: block;

  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }
`;

export const Link = styled.a`
  ${LinkStyles};
`;

export const RouteLink = styled(NavLink)`
  ${LinkStyles};
`;

export const EditorDiv = styled.div``;

export const ChevronWrapper = styled.span`
  ${svg(11, 6)}
  width: 11px;
  position: relative;
  top: -2px;

  path {
    transition: fill 0.2s linear;

    fill: ${({ theme }) => theme.colors.element.primary};
  }
`;

export const Trigger = styled.button`
  border: 0;
  padding: 0;
  background: none;
  cursor: pointer;
  ${({ theme }) => spacing({ theme, $mt: 2 })};
  ${({ theme }) => linkStyle(theme.colors.link.primary)};

  &:hover {
    ${ChevronWrapper} {
      path {
        fill: ${({ theme }) => theme.colors.link.primary.colorHover};
      }
    }
  }

  &:focus {
    ${safeFocusRemoval}
    ${focusOutline}
  }
  ${hideOnPrint}
`;

export const Heading = styled(Text)`
  h2& {
    ${heading_xl}
  }

  h3& {
    ${heading_l}
  }

  h4& {
    ${heading_m}
  }

  h5& {
    ${heading_s}
  }

  h6& {
    ${heading_xs}
  }

  h2&,
  h3&,
  h4& {
    ${({ theme }) => spacing({ theme, $py: 3 })}
  }

  h5&,
  h6& {
    ${({ theme }) => spacing({ theme, $py: 2 })}
  }
`;

export const RichText = styled(ContentRichText)`
`;
